import { z } from "zod";
import {
  MAX_RULE_EMAILS,
  NOTIFY_SUBJECT_LABELS,
  NOTIFY_SUBJECTS,
  REPEAT_WINDOW,
  RULE_CELLS,
  ruleCellLabel,
  type NotificationRule,
  type NotificationRulesView,
  type NotifyOutcome,
  type NotifySubject,
  type PutNotificationRulesBody,
} from "@shared/types/notifications";
import {
  OPEN_RECIPIENT_POLICY,
  OUTSIDE_ALLOWED_DOMAINS_MESSAGE,
  isExternalAddress,
  isRefusedAddress,
  type RecipientPolicy,
} from "@shared/types/notificationDomains";

/**
 * The Notifications tab's form model (contract-notif R4, R10): one rule per (What, When) cell
 * in `RULE_CELLS` order, and the repeat window. Pure, so the screen and its tests share one
 * definition of "changed", "valid" and "what Save sends".
 *
 * Addresses are edited as text, one per line, and turned into the body's list only when saved:
 * the server trims, lowercases and deduplicates, and the screen names the lines it will refuse
 * before sending anything.
 */

export interface RuleDraft {
  subject: NotifySubject;
  outcome: NotifyOutcome;
  enabled: boolean;
  emailsText: string;
  teams: boolean;
}

export interface RulesDraft {
  rules: RuleDraft[];
  repeatWindow: string;
}

export function cellKey(subject: NotifySubject, outcome: NotifyOutcome): string {
  return `${subject}.${outcome}`;
}

export function draftFromView(view: NotificationRulesView): RulesDraft {
  return {
    rules: RULE_CELLS.map(({ subject, outcome }) => {
      const rule = view.rules.find((r) => r.subject === subject && r.outcome === outcome);
      return {
        subject,
        outcome,
        enabled: rule?.enabled ?? false,
        emailsText: (rule?.emails ?? []).join("\n"),
        teams: rule?.teams ?? false,
      };
    }),
    repeatWindow: String(view.repeatWindowMinutes),
  };
}

/**
 * "Add a rule" (R4): "When a run fails" on under every subject the screen shows, the Teams switch
 * on when the instance has a webhook, no address; every other rule off. A subject the
 * screen hides (Data health while skyMonitor is off) stays off: a rule nobody saw is never
 * saved switched on (ADR 0007, no implicit default). Nothing is stored until Save.
 */
export function addRuleDraft(
  view: NotificationRulesView,
  subjects: readonly NotifySubject[],
): RulesDraft {
  const hasWebhook = view.teamsConfigured;
  return {
    rules: RULE_CELLS.map(({ subject, outcome }) => {
      const on = outcome === "failure" && subjects.includes(subject);
      return {
        subject,
        outcome,
        enabled: on,
        emailsText: "",
        teams: on && hasWebhook,
      };
    }),
    repeatWindow: String(view.repeatWindowMinutes),
  };
}

/** The non-empty lines of an address box, trimmed. */
export function emailLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

const emailSchema = z.email().max(320);

/**
 * The address box's own message: the lines that are not addresses, by line number, the cap, and
 * (#1111) while the tenant's Allowed notification domains is on, the lines outside it. Null when
 * the box is fine.
 */
export function emailsProblem(
  text: string,
  policy: RecipientPolicy = OPEN_RECIPIENT_POLICY,
  held: readonly string[] = [],
): string | null {
  // #1111: an address the rule already holds is kept even outside the list, as the server keeps
  // it (the runner skips it at send); only one a save adds is refused.
  const kept = new Set(held.map((address) => address.trim().toLowerCase()));
  const lines = text.split(/\r?\n/);
  const invalid: string[] = [];
  const outside: number[] = [];
  let count = 0;
  lines.forEach((raw, index) => {
    const line = raw.trim();
    if (line.length === 0) return;
    count += 1;
    if (!emailSchema.safeParse(line).success) invalid.push(`line ${index + 1} (${line})`);
    else if (isRefusedAddress(line, policy) && !kept.has(line.toLowerCase())) {
      outside.push(index + 1);
    }
  });
  const problems: string[] = [];
  if (invalid.length > 0) problems.push(`Not an email address: ${invalid.join(", ")}.`);
  if (outside.length === 1) problems.push(`Line ${outside[0]}: ${OUTSIDE_ALLOWED_DOMAINS_MESSAGE}`);
  if (outside.length > 1) {
    const last = outside[outside.length - 1];
    problems.push(
      `Lines ${outside.slice(0, -1).join(", ")} and ${last}: these addresses are outside the allowed domains for your organization.`,
    );
  }
  if (count > MAX_RULE_EMAILS) {
    problems.push(`At most ${MAX_RULE_EMAILS} addresses. This box has ${count}.`);
  }
  return problems.length > 0 ? problems.join(" ") : null;
}

export function repeatWindowProblem(text: string): string | null {
  const value = Number(text.trim());
  if (
    text.trim() === "" ||
    !Number.isInteger(value) ||
    value < REPEAT_WINDOW.min ||
    value > REPEAT_WINDOW.max
  ) {
    return `Enter a whole number from ${REPEAT_WINDOW.min} to ${REPEAT_WINDOW.max}.`;
  }
  return null;
}

/** The PUT body the draft stands for (the webhook never travels here, round-1 ruling 14). */
export function bodyFromDraft(
  draft: RulesDraft,
  expectedVersion: string | null,
): PutNotificationRulesBody {
  const rules: NotificationRule[] = draft.rules.map((rule) => ({
    subject: rule.subject,
    outcome: rule.outcome,
    enabled: rule.enabled,
    emails: emailLines(rule.emailsText).map((line) => line.toLowerCase()),
    teams: rule.teams,
  }));
  return {
    rules,
    repeatWindowMinutes: Number(draft.repeatWindow.trim()),
    expectedVersion,
  };
}

/** Whether the draft would change what is stored (a Save has something to say). */
export function draftDiffers(draft: RulesDraft, view: NotificationRulesView): boolean {
  if (!view.hasRules && draft.rules.some((rule) => rule.enabled)) return true;
  const stored = draftFromView(view);
  if (draft.repeatWindow.trim() !== stored.repeatWindow) return true;
  return draft.rules.some((rule, index) => {
    const before = stored.rules[index];
    if (!before) return true;
    return (
      rule.enabled !== before.enabled ||
      rule.teams !== before.teams ||
      emailLines(rule.emailsText).join("\n") !== emailLines(before.emailsText).join("\n")
    );
  });
}

/** The button's words and the sentence always shown beside it (round 3, owner 2026-09-30). */
export const COPY_RULE_LABEL = "Use these recipients for every rule";
export const COPY_RULE_HELP =
  "Copies this rule's addresses and Teams setting to every other rule that fires at the same time (fails, has warnings, succeeds). Nothing is saved until you choose Save changes.";

/**
 * "Use these recipients for every rule" (round-2 critic ruling 2; named in round 3): the rule's
 * on/off, addresses and Teams switch go to the same "When" rule of the other subjects shown. A
 * subject the screen does not show (Data health while skyMonitor is off) is left as it is. There
 * is no L1 switch to copy: the L1 team hears every failure (round 3).
 */
export function copyToEverySubject(
  draft: RulesDraft,
  from: { subject: NotifySubject; outcome: NotifyOutcome },
  subjects: readonly NotifySubject[] = NOTIFY_SUBJECTS,
): RulesDraft {
  const source = draft.rules.find((r) => r.subject === from.subject && r.outcome === from.outcome);
  if (!source) return draft;
  return {
    ...draft,
    rules: draft.rules.map((rule) =>
      rule.outcome === from.outcome &&
      rule.subject !== from.subject &&
      subjects.includes(rule.subject)
        ? {
            ...rule,
            enabled: source.enabled,
            emailsText: source.emailsText,
            teams: source.teams,
          }
        : rule,
    ),
  };
}

/**
 * The "What" sections the Notifications tab shows: every subject, but Data health only while
 * skyMonitor is on (`features.flags.monitor`, spec section 11).
 */
export function shownSubjects(monitorOn: boolean): NotifySubject[] {
  return NOTIFY_SUBJECTS.filter((subject) => subject !== "data_health" || monitorOn);
}

/** "Copied to Transformations, Schedules and Credentials. Save changes to keep it." */
export function copiedSentence(
  from: NotifySubject,
  subjects: readonly NotifySubject[] = NOTIFY_SUBJECTS,
): string {
  const others = subjects
    .filter((subject) => subject !== from)
    .map((subject) => NOTIFY_SUBJECT_LABELS[subject]);
  const last = others.pop();
  const list = others.length > 0 ? `${others.join(", ")} and ${last}` : (last ?? "");
  return `Copied to ${list}. Save changes to keep it.`;
}

/**
 * Server validation details mapped onto the address boxes: `rules.4.emails.2` is the fifth cell
 * (What: Transformations, When a run has warnings), line 3.
 */
export function fieldErrorsFromDetails(
  details: ReadonlyArray<{ field?: string; issue: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const detail of details) {
    const match = /^rules\.(\d+)\.emails(?:\.(\d+))?$/.exec(detail.field ?? "");
    if (!match) continue;
    const cell = RULE_CELLS[Number(match[1])];
    if (!cell) continue;
    const key = cellKey(cell.subject, cell.outcome);
    const line = match[2] === undefined ? null : Number(match[2]) + 1;
    const sentence = line === null ? detail.issue : `Line ${line}: ${detail.issue}`;
    out[key] = out[key] ? `${out[key]} ${sentence}` : sentence;
  }
  return out;
}

/**
 * Review r1 S3: every rule's addresses are sent, the off ones included, but an off rule shows no
 * address box. When a refused cell is off, the save bar names it and says how to reach the box.
 * Null when every refused cell is on (its box shows the message).
 */
export function offRulesSentence(
  draft: RulesDraft,
  refusedCells: readonly string[],
): string | null {
  const off = draft.rules.filter(
    (rule) => !rule.enabled && refusedCells.includes(cellKey(rule.subject, rule.outcome)),
  );
  if (off.length === 0) return null;
  const names = off.map((rule) => ruleCellLabel(rule.subject, rule.outcome)).join(" · ");
  return off.length === 1
    ? `${names} is off and has an address to fix. Turn it on to see it.`
    : `${names} are off and have addresses to fix. Turn them on to see them.`;
}

/**
 * True when an enabled rule reaches somebody of this instance's: an address, or the Teams channel
 * when the instance has a webhook (with none, the Teams target is written `skipped`, so it reaches
 * no one). The L1 team is not counted: it is Skypoint's, and it hears failures whatever the rules.
 */
function ruleReachesSomeone(
  rule: Pick<RuleDraft, "emailsText" | "teams">,
  hasWebhook: boolean,
): boolean {
  return emailLines(rule.emailsText).length > 0 || (rule.teams && hasWebhook);
}

/**
 * §4.5 "rules on, every target of every enabled rule empty": at least one rule is on and none of
 * the rules that are on reaches anyone.
 */
export function nobodyNotified(view: NotificationRulesView): boolean {
  if (!view.hasRules) return false;
  const enabled = draftFromView(view).rules.filter((rule) => rule.enabled);
  const hasWebhook = view.teamsConfigured;
  return enabled.length > 0 && !enabled.some((rule) => ruleReachesSomeone(rule, hasWebhook));
}

/** A subject section's status (round 3): the header says it in words beside its colour. */
export type SubjectStatus = "active" | "no_recipient" | "off";

export const SUBJECT_STATUS_WORDS: Record<SubjectStatus, string> = {
  active: "Active",
  no_recipient: "No recipient",
  off: "Off",
};

function subjectStatus(
  enabled: readonly Pick<RuleDraft, "emailsText" | "teams">[],
  hasWebhook: boolean,
): SubjectStatus {
  if (enabled.length === 0) return "off";
  return enabled.some((rule) => ruleReachesSomeone(rule, hasWebhook)) ? "active" : "no_recipient";
}

/**
 * A subject section's header (round 3, owner 2026-09-30: "collapse what imports, what
 * transformations etc."): how many of its rules are on, and its status. Active when a rule that
 * is on reaches somebody, No recipient when rules are on and none of them does, Off otherwise.
 */
export function subjectSummary(
  draft: RulesDraft,
  subject: NotifySubject,
  hasWebhook: boolean,
): { on: number; total: number; label: string; status: SubjectStatus } {
  const rules = draft.rules.filter((rule) => rule.subject === subject);
  const enabled = rules.filter((rule) => rule.enabled);
  const status = subjectStatus(enabled, hasWebhook);
  return {
    on: enabled.length,
    total: rules.length,
    label: `${enabled.length} of ${rules.length} on`,
    status,
  };
}

/**
 * The sections open when the form first shows: the subjects with a rule on, in screen order; with
 * none on (a new instance), Imports, the first.
 */
export function openSubjects(
  draft: RulesDraft,
  subjects: readonly NotifySubject[],
): NotifySubject[] {
  const on = subjects.filter((subject) =>
    draft.rules.some((rule) => rule.subject === subject && rule.enabled),
  );
  if (on.length > 0) return on;
  return subjects.includes("import") ? ["import"] : subjects.slice(0, 1);
}

/**
 * The shown subjects with a rule that needs a fix (round 3 review S2), in screen order: an address
 * the page itself refuses (not in read-only, where nothing is typed) or one the server refused
 * (`fieldErrors`, keyed by `cellKey`). A closed section's rules are not in the page, so its header
 * says "Needs a fix" and a refused Save opens it: "They are marked above" is then true.
 */
export function subjectsNeedingFix(
  draft: RulesDraft,
  subjects: readonly NotifySubject[],
  fieldErrors: Readonly<Record<string, string>>,
  readOnly: boolean,
  policy: RecipientPolicy = OPEN_RECIPIENT_POLICY,
  held: ReadonlyMap<string, readonly string[]> = new Map(),
): NotifySubject[] {
  return subjects.filter((subject) =>
    draft.rules.some(
      (rule) =>
        rule.subject === subject &&
        ((!readOnly &&
          emailsProblem(
            rule.emailsText,
            policy,
            held.get(cellKey(rule.subject, rule.outcome)) ?? [],
          ) !== null) ||
          fieldErrors[cellKey(rule.subject, rule.outcome)] !== undefined),
    ),
  );
}

/** #1111: each rule's stored addresses, by `cellKey`: the ones a save may keep (`emailsProblem`). */
export function heldAddresses(view: NotificationRulesView): Map<string, readonly string[]> {
  return new Map(view.rules.map((rule) => [cellKey(rule.subject, rule.outcome), rule.emails]));
}

/**
 * #1111 (item 3 of the owner decision): the addresses the screen marks External, in the box's
 * order. While the tenant's Allowed notification domains is on, those outside it; while it is
 * off, those whose domain no person with access to the instance signs in with.
 */
export function externalAddresses(addresses: readonly string[], policy: RecipientPolicy): string[] {
  return addresses.filter((address) => isExternalAddress(address, policy));
}

/** The line under an address box, or null when no address is External. */
export function externalLine(addresses: readonly string[], policy: RecipientPolicy): string | null {
  const external = externalAddresses(addresses, policy);
  if (external.length === 0) return null;
  const reason = policy.allowedDomains.enabled
    ? "outside the allowed domains for your organization"
    : "no one with access to this instance uses this domain";
  return `External (${reason}): ${external.join(", ")}`;
}
